import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { DiaryNotesResponse } from 'src/app/models/diary-notes/diary-note.model';
import { DiaryNotesService } from 'src/app/services/diaryNotes/diary-notes.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import * as diaryNotesActions from 'src/app/store/actions/diary-notes.action';
import * as userActions from 'src/app/store/actions/user.action';
import { GeneralError } from 'vidis-common';
import {
  selectActiveFilters,
  selectPagination,
  selectSortData
} from '../selectors/diary-notes.selector';
import { selectPatientSSIN } from '../selectors/user.selector';

@Injectable()
export class DiaryNotesEffects {

    constructor(
    private actions$: Actions,
    private diaryNotesService: DiaryNotesService,
    private store: Store,
    private toastService: ToastService,
    ) {
    }

    fetchDiaryNotes$ = createEffect(() => this.actions$.pipe(
        ofType(diaryNotesActions.fetchDiaryNotesAction),
        withLatestFrom(
            this.store.select(selectPatientSSIN),
            this.store.select(selectPagination),
            this.store.select(selectSortData),
            this.store.select(selectActiveFilters)
        ),
        switchMap(([action, ssin, pagination, sortData, activeFilters]) =>
            this.diaryNotesService.getPatientsDiaryNotes(ssin, activeFilters, action.page, pagination.pageSize, sortData)
                .pipe(
                    switchMap((diaryNotes: DiaryNotesResponse) => [
                        diaryNotes.errors?.length > 0 && diaryNotes.items?.length > 0
                            ? diaryNotesActions.setDiaryNotesErrorsAction({diaryNotesErrors: diaryNotes.errors})
                            : diaryNotesActions.clearDiaryNotesErrorAction(),
                        diaryNotesActions.setDiaryNotesAction({
                            diaryNotes: diaryNotes.items,
                            availableFilters: diaryNotes.availableFilters,
                            page: diaryNotes.page,
                            totalPages: diaryNotes.totalPages,
                            totalItems: diaryNotes.totalItems
                        })
                    ]),
                  catchError(err => this.handleError(err))
                ))
    ));

  fetchCountDiaryNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(diaryNotesActions.fetchDiaryNotesCountAction),
      withLatestFrom(this.store.select(selectPatientSSIN)),
      switchMap(([action, ssin]) => {
        if (ssin !== undefined) {
          return this.diaryNotesService.countDiaryNotes(ssin).pipe(
            switchMap(countResult => [
              diaryNotesActions.setDiaryNotesCountAction({countResult})
            ]),
            catchError(err => this.handleError(err))
          );
        }
        return EMPTY;
      })
    )
  );

    postDiaryNote$ = createEffect(() => this.actions$.pipe(
        ofType(diaryNotesActions.postDiaryNoteAction),
        switchMap(content => this.diaryNotesService.postDiaryNote(content.diaryNoteContent, content.fileContent)
            .pipe(
              switchMap(() => [
                  diaryNotesActions.fetchDiaryNotesAction({}),
                  diaryNotesActions.incrementDiaryNotesCountAction()
                ]),
                catchError(err => of(diaryNotesActions.postDiaryNotesErrorAction({diaryNotesError: err.errors}))),
            ))
    ));

    postDiaryNoteErrorToaster$ = createEffect(() => this.actions$.pipe(
        ofType(diaryNotesActions.postDiaryNotesErrorAction),
        switchMap(async content => {
            let errorTranslationKey;
            switch (content?.diaryNotesError?.code) {
            case 'VIDIS_0410':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_0410';
                break;
            case 'VIDIS_0415':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_0415';
                break;
            case 'VIDIS_0420':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_0420';
                break;
            case 'VIDIS_0425':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_0425';
                break;
            case 'VIDIS_1005':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_1005';
                break;
            case 'VIDIS_1000':
                errorTranslationKey = 'diaryNotesPage.add-note-error.VIDIS_1000';
                break;
            default:
                errorTranslationKey = 'diaryNotesPage.add-note-error.default';
                break;
            }
            this.toastService.showError(errorTranslationKey);
        })
    ), {dispatch: false});

    diaryNoteDelete$ = createEffect(() => this.actions$.pipe(
        ofType(diaryNotesActions.diaryNoteDeleteAction),
        switchMap(content => this.diaryNotesService.deleteDiaryNote(content.id, content.version, content.source, content.pationtSsin)
            .pipe(
                map(response => diaryNotesActions.diaryNoteDeleteSucceededAction({
                    id: content.id, newNote: response.diaryNote, status: response.statusCode})),
                catchError(err => of(diaryNotesActions.diaryNoteDeleteFailedAction({error: err.error})))
            ))
    ));

    updatePatientNameFromDiaryNote$ = createEffect(() => this.actions$.pipe(
        ofType(diaryNotesActions.setDiaryNotesAction),
        filter(content => content.diaryNotes?.length > 0),
        switchMap(content => of(userActions.setPatientAction({patient: content.diaryNotes[0].subject})))
    ));

  private handleError(err: HttpErrorResponse): Observable<any> {
    let error: GeneralError = {code: 'VIDIS_0400'};
    if (err.status === 0) {
      error.code = 'network_error';
    } else if (err?.error?.code?.includes('VIDIS')) {
      error = err.error;
    }
    error.traceId = err.headers?.get('trace-id');

    return of(diaryNotesActions.setDiaryNotesErrorsAction({diaryNotesErrors: [error] }));
  }
}
